import { Button, Row, Col, Form, Input, Spin, Typography } from "antd";
import api from "ApiClient";
import AuthHelper from "components/auth/helpers";
import { useCallback, useEffect, useState } from "react";
import { ApiURL, AppURL } from "utils/Actions";
import { useHistory, useLocation } from "react-router-dom";
import { common } from "utils/Wording";
import { handleRedirect } from "utils/hooks/handleRedirect";
import { createGetRequest } from "utils/hooks/apiActions";
import { Helmet } from "react-helmet";
import useQuery from "utils/hooks/useQuery";
import { TokenResultCode } from "utils/Types";
import PasswordRequirementsHelp, {
  PasswordMatch,
  PASSWORD_REQUIREMENTS
} from "components/auth/password-requirements-help";
import PageLayout from "../page-layout";
import { isEmpty } from "lodash";
import { Trans, useTranslation } from "react-i18next";
import { ValidateErrorEntity } from "rc-field-form/lib/interface";
const { Text } = Typography;

const checkPasswordPassesRequirements = (input: string) => {
  let valid = true;
  Object.values(PASSWORD_REQUIREMENTS).forEach(pr => {
    if (isEmpty(input.match(pr.regex))) {
      valid = false;
    }
  });
  return valid;
};

const SetPassword: React.FC = () => {
  const { t } = useTranslation();
  const query = useQuery();
  const history = useHistory();
  const location = useLocation();
  const path = location.pathname + location?.search;
  const [form] = Form.useForm();
  const password = Form.useWatch("password", form);
  const confirmPassword = Form.useWatch("password2", form);
  const { isFieldTouched } = form;

  const [isTokenValid, setIsTokenValid] = useState(true);
  // need this field, because 'isFieldTouched' is not triggered
  const [forgotToEnterConfirmPassword, setForgotToEnterConfirmPassword] = useState(false);
  const [isTokenApiLoading, setIsTokenApiLoading] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [email, setEmail] = useState<string>();
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [errorType, setErrorType] = useState<TokenResultCode | undefined>(undefined);
  const token = query.get("token");

  const showPasswordsMatch = isFieldTouched("password2") || forgotToEnterConfirmPassword;

  useEffect(() => {
    checkValidToken();
  }, [token]);

  useEffect(() => {
    handleRedirect(history, path);
  }, []);

  const onSuccess = () => {
    const auth = AuthHelper.getAuth();
    if (!isEmpty(auth)) {
      setShowLoginPrompt(true);
    }
  };

  useEffect(() => {
    window.addEventListener("storage", onSuccess);
    return () => {
      window.removeEventListener("storage", onSuccess);
    };
  }, [onSuccess]);

  const checkValidToken = async () => {
    setIsTokenApiLoading(true);
    const newQueryParam = `?t=${token}&a=reset`;
    try {
      const tokenResult = await createGetRequest({ path: `${ApiURL.validToken}${newQueryParam}` });
      const tokenResultCode = tokenResult.data.data.data.resultCode;
      const user = tokenResult.data.data.data.user;
      setEmail(user);
      if (tokenResultCode !== TokenResultCode.VALID) {
        setErrorType(tokenResultCode);
        throw new Error(common.failAsync);
      }
    } catch (error: any) {
      setIsTokenValid(false);
      console.error(error);
    } finally {
      setIsTokenApiLoading(false);
    }
  };

  const handleSetPasswordFailed = (error: any) => {
    setIsTokenValid(false);
    console.error(error);
    AuthHelper.clearAuth();
  };

  const onResetPassword = () => {
    setIsResetting(true);
    form
      .validateFields()
      .then(async values => {
        const token = query.get("token");
        const endpoint = `${ApiURL.updatePasswordToken}?t=${token}&a=reset`;
        try {
          const res = await api.patch(endpoint, { ...values, username: email });
          const data = await res?.data;
          if (data) {
            AuthHelper.setAuth(data);
            AuthHelper.extendTimeout(true);
          } else {
            throw Error("Unable to reset password.");
          }
        } catch (error) {
          handleSetPasswordFailed(error);
          return false;
        } finally {
          setIsResetting(false);
        }
      })
      .catch(error => {
        handleSetPasswordFailed(error);
        return false;
      })
      .finally(() => {
        setIsResetting(false);
      });
  };

  const handleFormError = ({ values }: ValidateErrorEntity) => {
    if (values.password2 === undefined) {
      setForgotToEnterConfirmPassword(true);
    } else {
      setForgotToEnterConfirmPassword(false);
    }
  };

  const handleClickLogin = useCallback(() => {
    handleRedirect(history);
  }, []);

  if (!isTokenValid) {
    let errorKey: string | undefined = errorType;
    if (!errorType) {
      errorKey = "GENERIC";
    }

    return (
      <PageLayout isSingleCard={true}>
        <div style={{ textAlign: "center" }}>
          <h1 style={{ marginBottom: 32 }}>{t(`setPassword.errors.${errorKey}.heading`)}</h1>
          <Trans>
            <p style={{ textAlign: "center", marginBottom: 32 }}>
              {t(`setPassword.errors.${errorKey}.message`)}
            </p>
          </Trans>

          {errorType === TokenResultCode.EXPIRED && (
            <>
              <Button
                type="primary"
                block
                onClick={() => {
                  history.push(AppURL.forgetPassword);
                }}
                style={{ marginBottom: 24 }}
              >
                Request new reset instructions
              </Button>
              <Button
                block
                type="link"
                onClick={() => {
                  history.push(AppURL.login);
                }}
              >
                Back to login
              </Button>
            </>
          )}

          {errorType === TokenResultCode.INVALID && (
            <>
              <Button
                block
                type="primary"
                style={{ marginBottom: 16 }}
                onClick={() => {
                  history.push(AppURL.login);
                }}
              >
                Log in
              </Button>
              <Button
                block
                type="link"
                onClick={() => {
                  history.push(AppURL.forgetPassword);
                }}
              >
                Set password
              </Button>
            </>
          )}
        </div>
      </PageLayout>
    );
  }

  if (showLoginPrompt) {
    return (
      <PageLayout>
        <h1> {t("setPassword.showLoginPrompt.title")}</h1>
        <p className="text-center"> {t("setPassword.showLoginPrompt.description")}</p>
        <Button type="primary" onClick={handleClickLogin} block>
          {t("setPassword.showLoginPrompt.CTA")}
        </Button>
      </PageLayout>
    );
  }

  return (
    <>
      <Helmet>
        <title>{t("helmet.setPassword.title")}</title>
        <meta name="description" content={t("helmet.setPassword.meta") || ""} />
      </Helmet>
      <PageLayout isSingleCard={true}>
        {isTokenApiLoading ? (
          <Spin size="large" className="center-content" style={{ height: "50vh" }} />
        ) : (
          <Row style={{ width: "100%", height: "100%" }}>
            <Col xs={24}>
              <>
                <h1 className="text-center">{t("setPassword.title")}</h1>
                <Form
                  layout="vertical"
                  form={form}
                  onFinish={onResetPassword}
                  onFinishFailed={handleFormError}
                  requiredMark={false}
                  validateTrigger={["onSubmit"]}
                >
                  <div style={{ marginBottom: 24 }}>
                    <div>Email</div>
                    <Text type="secondary">{email}</Text>
                  </div>
                  <Form.Item
                    label="Create password"
                    name="password"
                    rules={[
                      { required: true, message: "Please input your password." },
                      () => ({
                        validator(_, value = "") {
                          const valid = checkPasswordPassesRequirements(value);
                          if (valid) {
                            return Promise.resolve();
                          } else {
                            return Promise.reject();
                          }
                        }
                      })
                    ]}
                    validateTrigger="onBlur"
                    style={{ marginBottom: 8 }}
                  >
                    <Input.Password />
                  </Form.Item>
                  <PasswordRequirementsHelp input={password} />

                  <Form.Item
                    label="Confirm new password"
                    name="password2"
                    dependencies={["password"]}
                    validateTrigger={["onBlur", "onChange", "onSubmit"]}
                    rules={[
                      { required: true },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (!value || getFieldValue("password") === value) {
                            return Promise.resolve();
                          }
                          return Promise.reject();
                        }
                      })
                    ]}
                    help={
                      <div>
                        {showPasswordsMatch ? (
                          <PasswordMatch password={password} confirm={confirmPassword} />
                        ) : null}
                      </div>
                    }
                  >
                    <Input.Password
                      onPaste={e => {
                        e.preventDefault();
                        return false;
                      }}
                      onCopy={e => {
                        e.preventDefault();
                        return false;
                      }}
                    />
                  </Form.Item>

                  <Form.Item>
                    <Button
                      type="primary"
                      htmlType="submit"
                      block
                      loading={isResetting}
                      disabled={isResetting}
                    >
                      {t("setPassword.resetCTA")}
                    </Button>
                  </Form.Item>
                </Form>
              </>
            </Col>
          </Row>
        )}
      </PageLayout>
    </>
  );
};

export default SetPassword;
